import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouteError, isRouteErrorResponse } from 'react-router-dom'
import {
  AlertTriangleIcon,
  CheckIcon,
  Copy01Icon,
  Home01Icon,
  RefreshCcw01Icon,
} from '@untitledui-icons/react/line'
import * as Sentry from '@sentry/react'
import { Logo } from '@/components/layout/Logo'

/**
 * Information extracted from a thrown value so the fallback UI can render it
 * consistently regardless of source (Error, RouteErrorResponse, string, etc).
 */
interface ParsedError {
  message?: string
  stack?: string
  /** Status label like "500 Internal Server Error" – only set for route errors */
  statusLabel?: string
}

function parseError(error: unknown): ParsedError {
  if (isRouteErrorResponse(error)) {
    const data = error.data as { message?: string } | string | undefined
    const message =
      (typeof data === 'object' && data?.message) ||
      (typeof data === 'string' ? data : undefined) ||
      error.statusText
    return {
      statusLabel: `${error.status} ${error.statusText}`,
      message,
    }
  }
  if (error instanceof Error) {
    return { message: error.message, stack: error.stack }
  }
  if (typeof error === 'string') {
    return { message: error }
  }
  return {}
}

export interface ErrorFallbackProps {
  error: unknown
  /**
   * Optional reset callback. When provided, the "Try again" button calls this
   * (e.g. Sentry.ErrorBoundary's resetError). When omitted, the button performs
   * a hard reload.
   */
  resetError?: () => void
  /**
   * Sentry event ID for this error. When present, a "reference ID" block is
   * shown so users can quote it to support and we can match the event in
   * Sentry. Will be undefined when Sentry is disabled (e.g. in dev without
   * VITE_SENTRY_ENABLED).
   */
  eventId?: string
}

interface ErrorReferenceProps {
  eventId: string
}

/**
 * Renders the Sentry event ID with a copy-to-clipboard button. Falls back to
 * selectable text if the Clipboard API isn't available (e.g. non-https in
 * older browsers).
 */
function ErrorReference({ eventId }: ErrorReferenceProps) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(eventId)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard API unavailable; the user can still select the text.
    }
  }

  return (
    <div className="mt-8 rounded-lg border border-gray-200 bg-white p-4 text-left dark:border-gray-700 dark:bg-gray-900">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {t('errors.error_reference')}
      </p>
      <div className="flex items-center gap-2">
        <code className="flex-1 select-all break-all rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-800 dark:bg-gray-800 dark:text-gray-200">
          {eventId}
        </code>
        <button
          type="button"
          onClick={handleCopy}
          aria-label={t('errors.copy')}
          className="
            inline-flex items-center gap-1.5 rounded-md
            border border-gray-300 bg-white px-2.5 py-1.5
            text-xs font-semibold text-gray-700
            transition-colors hover:bg-gray-50
            focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2
            dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700
          "
        >
          {copied ? (
            <CheckIcon className="h-3.5 w-3.5 text-success-600 dark:text-success-400" />
          ) : (
            <Copy01Icon className="h-3.5 w-3.5" />
          )}
          {copied ? t('errors.copied') : t('errors.copy')}
        </button>
      </div>
      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
        {t('errors.error_reference_help')}
      </p>
    </div>
  )
}

/**
 * Branded full-page error fallback. Renders the same way for any error source
 * (top-level boundary, route error, etc) and only exposes the technical details
 * in development.
 *
 * Note: uses plain <a> instead of <Link> because this component must also work
 * when rendered outside the router context (e.g. from the root boundary in
 * main.tsx, or when the router itself failed to mount).
 */
export function ErrorFallback({ error, resetError, eventId }: ErrorFallbackProps) {
  const { t } = useTranslation()
  const isDev = import.meta.env.DEV
  const parsed = parseError(error)

  const handleTryAgain = () => {
    if (resetError) {
      resetError()
      return
    }
    window.location.reload()
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-cream px-4 py-12 dark:bg-gray-950">
      <div className="mb-8">
        <Logo width={140} />
      </div>

      <div className="w-full max-w-2xl text-center">
        <div className="mb-6 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-error-100 dark:bg-error-900/30">
            <AlertTriangleIcon className="h-8 w-8 text-error-600 dark:text-error-400" />
          </div>
        </div>

        {parsed.statusLabel && (
          <p className="mb-2 text-sm font-semibold text-error-600 dark:text-error-400">
            {parsed.statusLabel}
          </p>
        )}

        <h1 className="mb-4 text-4xl font-bold text-gray-900 dark:text-white sm:text-5xl">
          {t('errors.something_went_wrong')}
        </h1>
        <p className="mb-8 text-lg text-gray-500 dark:text-gray-400">
          {t('errors.something_went_wrong_description')}
        </p>

        {isDev && (parsed.message || parsed.stack) && (
          <details className="mx-auto mb-8 max-w-full text-left">
            <summary className="cursor-pointer text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t('errors.error_details')}
            </summary>
            <pre className="mt-2 max-h-96 overflow-auto rounded-lg border border-gray-300 bg-gray-50 p-3 text-xs text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
              {parsed.message}
              {parsed.stack ? `\n\n${parsed.stack}` : ''}
            </pre>
          </details>
        )}

        <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={handleTryAgain}
            className="
              inline-flex items-center gap-2 rounded-lg
              border border-gray-300 bg-white px-4 py-2.5
              text-sm font-semibold text-gray-700
              transition-colors hover:bg-gray-50
              focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2
              dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700
            "
          >
            <RefreshCcw01Icon className="h-4 w-4" />
            {t('errors.try_again')}
          </button>
          <a
            href="/dashboard"
            className="
              inline-flex items-center gap-2 rounded-lg
              bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white
              transition-colors hover:bg-brand-600
              focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2
            "
          >
            <Home01Icon className="h-4 w-4" />
            {t('errors.go_home')}
          </a>
        </div>

        {eventId && <ErrorReference eventId={eventId} />}
      </div>
    </div>
  )
}

/**
 * Top-level error boundary. Wraps the entire app and reports component-level
 * render errors to Sentry. Use this in main.tsx around <App />.
 */
export function ErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError, eventId }) => (
        <ErrorFallback
          error={error}
          resetError={resetError}
          // Sentry hands us an eventId even when it's been initialized with
          // `enabled: false` (events get dropped silently). Use isEnabled()
          // — which also checks the client's enabled flag and transport — so
          // we never show a reference ID that doesn't exist on our side.
          eventId={Sentry.isEnabled() ? eventId : undefined}
        />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}

/**
 * Reports a route-level error to Sentry and returns the event ID. Returns
 * undefined when there's nothing useful to report (e.g. plain 404s) or when
 * Sentry is disabled (enabled: false / no transport), to avoid showing the
 * user a reference ID that was never actually sent.
 */
function reportRouteErrorToSentry(error: unknown): string | undefined {
  let id: string | undefined

  if (error instanceof Error) {
    id = Sentry.captureException(error)
  } else if (isRouteErrorResponse(error)) {
    // Don't spam Sentry for plain 404s; only report unexpected statuses.
    if (error.status >= 500) {
      id = Sentry.captureException(
        new Error(`Route error ${error.status}: ${error.statusText}`)
      )
    }
  } else if (error !== undefined && error !== null) {
    id = Sentry.captureException(new Error(String(error)))
  }

  // captureException returns a UUID even when the SDK is disabled (init() was
  // called with enabled: false). isEnabled() additionally checks the client's
  // enabled flag and transport, so the ID we surface is one that was actually
  // sent.
  return id && Sentry.isEnabled() ? id : undefined
}

/**
 * React Router `errorElement`. Catches errors thrown during route loading or
 * rendering and reports them to Sentry, then renders the same branded fallback.
 */

export function RouteErrorElement() {
  const error = useRouteError()

  // Capture once per component instance via a lazy state initializer.
  // Sentry.ErrorBoundary handles render errors that propagate up the React
  // tree, but errors caught by React Router never reach it — we report them
  // manually here and keep the returned event ID for the fallback UI.
  const [eventId] = useState(() => reportRouteErrorToSentry(error))

  return <ErrorFallback error={error} eventId={eventId} />
}

export default ErrorBoundary
